/** @jsxImportSource @emotion/react */
import React from "react";
import {
  Select,
  Typography,
  Row,
  Col,
  Input,
  InputNumber,
  Button,
  Tag,
} from "antd";
import * as Styles from "./styles";
import { DimensionInput } from "../DimensionInput";

const KEYS = ["Coverage", "Dimension", "Id"];

const NUMERIC_KEYS = ["Coverage"];

const DIMENSION_KEY = "Dimension";

const KEY_OPTIONS = KEYS.map((key) => {
  return { label: key, value: key };
});

export interface OptionTagsInputProps {
  tags: Record<string, string | number>;
  displayKey: string;
  onTagsChange: (tags: Record<string, string | number>) => void;
  onDisplayKeyChange: (displayKey: string) => void;
}

export const OptionTagsInput = ({
  tags,
  displayKey,
  onTagsChange,
  onDisplayKeyChange,
}: OptionTagsInputProps) => {
  const [draftKey, setDraftKey] = React.useState("");
  const [draftValue, setDraftValue] = React.useState<string | number>("");

  const entries = Object.entries(tags);

  const handleKeyChange = (value: string) => {
    setDraftKey(value);
    setDraftValue("");
  };

  const handleNumberChange = (value?: string | number) => {
    setDraftValue(value ?? "");
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDraftValue(e.target.value);
  };

  const handleDimensionChange = (value: string) => {
    setDraftValue(value);
  };

  const handleAdd = () => {
    onTagsChange({ ...tags, [draftKey]: draftValue });
    setDraftKey("");
    setDraftValue("");
  };

  const handleDisplayKeyChange = (value?: string) => {
    onDisplayKeyChange(value ?? "");
  };

  const renderValueControl = () => {
    if (draftKey === DIMENSION_KEY) {
      return <DimensionInput onChange={handleDimensionChange} />;
    }

    if (NUMERIC_KEYS.includes(draftKey)) {
      return (
        <InputNumber
          css={Styles.fullWidthStyle}
          placeholder="Value"
          min={0}
          value={typeof draftValue === "number" ? draftValue : undefined}
          onChange={handleNumberChange}
        />
      );
    }

    return (
      <Input
        placeholder="Value"
        disabled={!draftKey}
        value={String(draftValue)}
        onChange={handleTextChange}
      />
    );
  };

  return (
    <>
      <Row gutter={8} align="middle" css={Styles.sectionStyle}>
        <Col span={6}>
          <Typography.Text type="secondary">Tags (optional)</Typography.Text>
        </Col>
        <Col span={7}>
          <Select
            css={Styles.fullWidthStyle}
            placeholder="Key"
            value={draftKey || undefined}
            onChange={handleKeyChange}
            options={KEY_OPTIONS}
          />
        </Col>
        <Col span={7}>{renderValueControl()}</Col>
        <Col span={4}>
          <Button disabled={!draftKey || draftValue === ""} onClick={handleAdd}>
            ADD
          </Button>
        </Col>
      </Row>
      {entries.length > 0 && (
        <div css={Styles.sectionStyle}>
          {entries.map(([key, value]) => {
            const handleClose = (e: React.MouseEvent<HTMLElement>) => {
              e.preventDefault();
              const remaining = { ...tags };
              delete remaining[key];
              onTagsChange(remaining);
            };
            return (
              <Tag key={key} closable onClose={handleClose} color="blue">
                {key}: {value}
              </Tag>
            );
          })}
        </div>
      )}
      <Row gutter={8} align="middle" css={Styles.sectionStyle}>
        <Col span={6}>
          <Typography.Text type="secondary">
            Option display (optional)
          </Typography.Text>
        </Col>
        <Col span={7}>
          <Select
            css={Styles.fullWidthStyle}
            allowClear
            placeholder="None"
            value={displayKey || undefined}
            onChange={handleDisplayKeyChange}
            options={KEY_OPTIONS}
          />
        </Col>
      </Row>
    </>
  );
};
